/**
 * Salty Cells — Car Compatibility Lookup
 *
 * SKUs match the live shop at saltycells.store/shop:
 *   SC-NA1260     → 12V 30Ah   compact cars              (30 Ah / 650 CCA)  A$275
 *   SC-NA1280     → 12V 60Ah   4WD & heavy duty          (60 Ah / 900 CCA)  A$429  [306x174x220mm JIS D31 case — Sep 2026 sixth pass: 40Ah → 60Ah per PI YC20260914001]
 *   SC-EU1280     → 12V 60Ah   European performance      (60 Ah / 900 CCA) A$435  [DIN75-class case] — renamed from SC-EU1288, Sep 2026 spec correction (88Ah → 80Ah)
 *   SC-NA1270-280 → 12V 50Ah   mid-size SUVs/sedans/Euro (50 Ah / 860 CCA)  A$389  [279x175x189mm case, DIN65/LN3-class, built-in BMS] — consolidated Aug 2026 from SC-NA1270 + SC-EU1275; Sep 2026 spec correction (75Ah → 70Ah)
 *   SC-NA1270-260 → 12V 35Ah   Full-size SUVs/wagons     (35 Ah / 700 CCA)  A$319  [260x173x220mm case, built-in BMS] — REMOVED FROM ACTIVE RANGE Sep 2026 at Jeremy's direction (no Yichen supplier line for this case size). Page kept live at /na1270-260 marked "Currently Unavailable", not sold. Its 5 carData vehicle mappings below are commented out so the fitment tool honestly reports "no match" rather than recommend an unbuyable product.
 *
 * Sep 2026 (fifth pass): ALL 5 live SKUs (NA1260, NA1260L, NA1270-280,
 * NA1280, EU1280) switched to pre-order only at Jeremy's direction —
 * `preorderOnly: true` + `waitDays: 30` added to each. Live Stripe "Buy Now"
 * checkout is no longer surfaced anywhere on-site (the lookup tool, product
 * pages, shop.html and index.html all now route to /preorder instead);
 * `stripeUrl` values are left in the data untouched so this can be flipped
 * back per-SKU without re-entering anything. Unlike SC-DC300 (a genuinely
 * unlaunched product with estimated specs/price), these 5 have confirmed
 * specs and prices — only fulfillment timing changed — so the pre-order
 * messaging says "reserve now, ships in ~30 days" rather than DC300's
 * "coming soon / price is an estimate" framing. SC-NA1270-260 is NOT
 * included — it's already `unavailable: true` (no Yichen supplier line at
 * all), a stronger state than pre-order, so it stays as-is. See
 * SaltyCells_All_Products_PreOrder_Sep2026.md.
 *
 * SC-NA1270, SC-EU1275, and SC-NA1275 were retired Aug 2026 and merged into
 * SC-NA1270-280 / SC-NA1270-260 — see
 * SaltyCells_NA1270_EU1275_NA1275_Consolidation_Aug2026.md.
 *
 * Sep 2026: SC-NA1270-280 corrected from 75Ah back to 70Ah (its original
 * pre-consolidation NA1270 spec) at Jeremy's direction. SC-EU1288 renamed to
 * SC-EU1280 and corrected from 88Ah to 80Ah, also at Jeremy's direction.
 * Both changes wired into the live Stripe products/payment links (Product
 * name/description/metadata updated in place — same Price/Payment Link IDs,
 * no price change). See SaltyCells_NA1270-280_EU1280_Spec_Corrections_Sep2026.md.
 *
 * Sep 2026 (second pass): Ah/CCA ratings reduced range-wide at Jeremy's
 * direction (previous figures were overspecced relative to the physical
 * cell count) and prices cut 20% across all 6 SKUs. New specs were set to
 * stay above AESON's equivalent-case-size competitor product (see
 * SaltyCells_vs_Aeson_Competitive_Update_Sep2026.docx for the case-matched
 * comparison this was benchmarked against) while landing meaningfully below
 * the prior claims. Wh recalculated at 12.4V nominal for each new Ah value.
 * Weight and case dimensions unchanged — this is a capacity-claim/price
 * correction on the same physical unit, not a new product. See
 * SaltyCells_Spec_Price_Revision_Sep2026.md.
 *
 * Sep 2026 (third pass): SC-NA1270-260 removed from the active range at
 * Jeremy's direction — Yichen has no matching supplier line for this
 * 260x173x220mm case size (flagged as an open risk in the Sep 2026 order
 * form). The `batteries` entry is kept (product page stays live, marked
 * "Currently Unavailable", not deleted/404'd) but all 5 of its carData
 * vehicle mappings (Toyota Kluger, Tarago, LandCruiser 100 Series Petrol V8;
 * Mazda CX-9 2007-2020; Lexus NX 2014-2021) are commented out below so the
 * fitment tool reports "no match" instead of recommending a battery that
 * can't currently be bought. See
 * SaltyCells_NA1270-260_Removed_From_Range_Sep2026.md.
 *
 * Sep 2026 (fourth pass): Ah corrected to match Yichen's real cell
 * increments (10Ah steps only, per PI YC20260911001 — no 5Ah tiers exist)
 * and prices rebuilt from that PI's actual landed cost to target a uniform
 * ~40% gross margin, at Jeremy's direction. NA1260/NA1260L's 30Ah already
 * matched Yichen exactly (no Ah change), but the other three all needed
 * rounding DOWN to Yichen's real capacity: NA1270-280 55→50Ah, NA1280
 * 45→40Ah, EU1280 65→60Ah. CCA scaled proportionally with Ah except
 * EU1280, which was deliberately held at 900 CCA rather than scaled down —
 * see that entry's own comment for why. Landed cost = PI unit price (USD)
 * + a 19-unit prorated share of the PI's DDP sea-freight line, converted at
 * ≈1.396 AUD/USD (11 Sep 2026 XE mid-market). Some prices moved DOWN
 * (NA1260/NA1260L/NA1280 were already running above 40% margin) and some
 * moved UP (NA1270-280/EU1280 were running ~28-29%) — this is a genuine
 * margin-normalization pass, not a straight price increase. Still pending:
 * Yichen's written confirmation of YCSTSOD1260's real case dimensions
 * (the PI's stated 278x175x190mm doesn't match our claimed 230x175x200mm
 * D23 case — see SaltyCells_Yichen_PI20260911001_Review_Sep2026.md) and of
 * exact polarity for the 50/40/60Ah lines. See
 * SaltyCells_Yichen_PI20260911001_Review_Sep2026.md and
 * SaltyCells_Spec_Price_Revision_Sep2026_v2.md.
 *
 * Sep 2026 (sixth pass — PI YC20260914001, 14 Sep 2026): the range now
 * mirrors Yichen's latest PI line-for-line, at Jeremy's direction.
 *   - SC-NA1280 moved 40Ah → 60Ah (Yichen now quotes 60Ah in the true
 *     306x174x220mm JIS D31 case, US$165.67/unit). Wh 496 → 744. CCA set to
 *     900 (matches the 60Ah EU1280; Yichen has NOT supplied a CCA figure for
 *     this cell — conservative, confirm against the datasheet). Price rebuilt
 *     to the ~40% margin target from the new landed cost: ~$257 AUD
 *     ((165.67 + 346.91/19) x 1.396) / 0.6 = ~$428 → $429. It would have run
 *     at only ~23% margin at the old $335.
 *   - Weights replaced with the PI's figures: NA1260/NA1260L 4.5 kg,
 *     NA1270-280 6.5 kg, NA1280 6.0 kg, EU1280 7.5 kg (no longer an estimate).
 *   - BMS: NA1270-280 and EU1280 are "bluetooth version BMS" on the PI;
 *     NA1280 is a standard BMS; NA1260/NA1260L list no BMS.
 *   - Polarity: PI now states all of lines 3-5 as Left "-" / Right "+" (RHP),
 *     closing the "pending confirmation" item — matches this file.
 *   - Warranty raised from 3 to 5 years on every SKU (site-wide change).
 *   - Case sizes/classes below are deliberately UNCHANGED — the fitment
 *     guide's Case Class sizing is the gospel size (Jeremy, 21 Sep 2026); the
 *     PI's 1-3mm differences (NA1270-280 279x175x190, EU1280 318x176x187) were
 *     accepted as tolerance.
 */

// Sep 2026 (21 Sep): stripeUrl values below are the NEW payment links for the
// PI YC20260914001 range (new Stripe products/prices: NA1260 $275, NA1260L $275,
// NA1270-280 $389, NA1280 $429, EU1280 $435; AU-only, $30 delivery rate). The
// previous links (old specs/prices) are still active in Stripe until deploy —
// see SaltyCells_PI20260914001_Range_Update_5yr_Warranty_Sep2026.md.
const batteries = {
  "SC-NA1260": {
    name:     "SaltyCell NA1260",
    group:    "12V 30Ah",
    cca:      650,
    ah:       30,
    wh:       372,
    weight:   "4.5 kg",
    l: 230, w: 175, h: 200,
    cycleLife: "3,000+",
    warranty:  "5 Years",
    price:    275,
    sku:      "SC-NA1260",
    tag:      "Compact Cars",
    caseClass: "JIS D23",
    stripeUrl: "/cart/50500652073175:1?checkout",
    preorderOnly: true, // Sep 2026 (5th pass): all live SKUs switched to pre-order, ~30 day wait — see header comment
    waitDays: 30,
    // Polarity: Left-Hand Positive (LHP) — corrected Aug 2026, see SC-NA1260L
    // entry below for the JIS-standard source. Fitment list rebuilt in the
    // same pass against real per-vehicle AU retailer/OEM sources.
    //
    // Sep 2026: Ah/CCA lowered 60→30 / 1300→650 and price cut 20%
    // (349→279) at Jeremy's direction — stays above AESON's case-matched
    // 55D23L (20Ah/450CCA/$318). See
    // SaltyCells_Spec_Price_Revision_Sep2026.md.
    //
    // Sep 2026 (4th pass): 30Ah already matches Yichen's real supply
    // exactly (PI YC20260911001, S/N 1-2), no Ah change. Price rebuilt from
    // that PI's actual landed cost (~$162 AUD incl. prorated freight) to
    // hit ~40% margin, at Jeremy's direction — moved DOWN 279→275 (the old
    // price was already running ~42% on the new lower cost basis). ⚠ PI's
    // stated case dimensions for this line (278x175x190mm) don't match the
    // 230x175x200mm claimed here — unresolved, see
    // SaltyCells_Yichen_PI20260911001_Review_Sep2026.md before ordering.
  },
  "SC-NA1270-280": {
    name:     "SaltyCell NA1270-280",
    group:    "12V 50Ah",
    cca:      860,
    ah:       50,
    wh:       620,
    weight:   "6.5 kg",
    l: 279, w: 175, h: 189,
    cycleLife: "3,000+",
    warranty:  "5 Years",
    price:    389,
    sku:      "SC-NA1270-280",
    tag:      "Mid-Size SUVs, Sedans & European Compact/Mid-Size",
    caseClass: "DIN65/LN3-class",
    features: "Built-in Bluetooth BMS — stop-start compatible, cell monitoring",
    stripeUrl: "/cart/50500652138711:1?checkout",
    preorderOnly: true, // Sep 2026 (5th pass): all live SKUs switched to pre-order, ~30 day wait — see header comment
    waitDays: 30,
    // Consolidated Aug 2026 at Jeremy's direction: merges the former
    // SC-NA1270 (279x175x189mm DIN65/LN3-class) and SC-EU1275
    // (278x175x190mm DIN65-class) into a single SKU — the two case sizes
    // are within 1-2mm of each other on every dimension, a genuine
    // physical match, not just a naming convenience. Now includes a
    // built-in BMS for stop-start compatibility and cell monitoring.
    // Former SC-EU1275 vehicles (previously preorder-only) are now live
    // under this SKU's existing Stripe checkout — a real go-live, not
    // just a rename. Fitment list below is the union of the two former
    // SKUs' confirmed vehicles. See
    // SaltyCells_NA1270_EU1275_NA1275_Consolidation_Aug2026.md.
    //
    // Sep 2026 correction at Jeremy's direction: Ah reverted from 75Ah
    // (the Aug 2026 consolidation's standardised-up figure, matching
    // EU1275's spec) back to 70Ah — NA1270's original, already-confirmed
    // spec (1,200 CCA, 11.2kg, 868 Wh @ 12.4V). This also matches what the
    // underlying live Stripe product (carried over unchanged from the
    // pre-consolidation SC-NA1270) had listed the whole time — the site
    // and Stripe had drifted apart at 75Ah vs 70Ah; both now read 70Ah.
    // See SaltyCells_NA1270-280_EU1280_Spec_Corrections_Sep2026.md.
    //
    // Aug 2026 BMS/stop-start audit: verified per-vehicle rather than
    // assumed. Genuine factory stop-start confirmed on Hyundai i40,
    // Mercedes C-Class (W205 C200), VW Golf Mk7, and likely VW Polo Mk6.
    // Audi A3/A4 only gained stop-start partway through their listed year
    // range (see comments on those entries). Jeep Compass and Nissan
    // Navara NP300 were found NOT to be stop-start equipped in AU spec —
    // confirmed by Jeremy from installer-network knowledge — and are
    // fitted here on case-size/polarity match only. The "stop-start
    // compatible" feature describes the BMS's real capability; it does not
    // claim every listed vehicle uses that capability. See
    // SaltyCells_NA1270_Stop_Start_BMS_Audit_Aug2026.md.
    //
    // Sep 2026: Ah/CCA lowered 70→55 / 1200→950 and price cut 20%
    // (397→318) at Jeremy's direction — stays above AESON's case-matched
    // H6/LN3 stop-start unit (40Ah/750CCA/$505). See
    // SaltyCells_Spec_Price_Revision_Sep2026.md.
    //
    // Sep 2026 (4th pass): Ah corrected 55→50 to match Yichen's real
    // supply (PI YC20260911001 S/N 3 quotes 50Ah for this exact case,
    // 279x175x190mm — only 5Ah short of what we'd claimed) — CCA scaled
    // proportionally 950→860, Wh recalculated at 12.4V. Still clears
    // AESON's H6/LN3 comparator (40Ah/750CCA) with room to spare. Price
    // rebuilt from real landed cost (~$232 AUD incl. prorated freight) to
    // hit ~40% margin at Jeremy's direction — moved UP 318→389 (this SKU
    // was running only ~28% margin at the old price/cost). Polarity on
    // this PI line is still "pending confirmation" from Yichen — Jeremy's
    // call to assume RHP (matches this SKU's established polarity) pending
    // written confirmation. See
    // SaltyCells_Yichen_PI20260911001_Review_Sep2026.md.
  },
  "SC-NA1280": {
    name:     "SaltyCell NA1280",
    group:    "12V 60Ah",
    cca:      900, // Sep 2026 (6th pass): set to match the 60Ah EU1280 — Yichen has not supplied a CCA for this cell; confirm vs datasheet
    ah:       60,
    wh:       744,
    weight:   "6.0 kg",
    l: 306, w: 174, h: 220,
    cycleLife: "3,000+",
    warranty:  "5 Years",
    price:    429, // Sep 2026 (6th pass): rebuilt to ~40% margin on PI YC20260914001 landed cost (was 335 at 40Ah)
    sku:      "SC-NA1280",
    tag:      "4WD & Heavy Duty",
    caseClass: "JIS D31",
    features: "Built-in BMS — cell monitoring",
    stripeUrl: "/cart/50500652171479:1?checkout",
    preorderOnly: true, // Sep 2026 (5th pass): all live SKUs switched to pre-order, ~30 day wait — see header comment
    waitDays: 30,
    // Polarity determined Aug 2026 (first audit — previously unverified):
    // a 5-stream fitment audit found NA1280's confirmed D31-class matches
    // split by JIS suffix, roughly 13 "L"-suffix (e.g. 115D31L) vehicles to
    // 4 no-suffix/"R" (e.g. 115D31R/N70ZZ) vehicles. Applying the same
    // convention established for NA1260/NA1260L (L suffix = RHP), the
    // majority — and therefore SaltyCells' physical NA1280 stock — is
    // Right-Hand Positive. The minority no-suffix/LHP vehicles (HiLux
    // 2016+, LandCruiser 70 & 300 Series, Nissan Patrol Y62) are a genuine
    // size match but wrong polarity, and have been removed rather than
    // listed incorrectly — a future SC-NA1280L (LHP) product would recapture
    // them. Fitment list also pruned from 72 to 17 confirmed 306x174x220mm
    // matches in the same pass — see SaltyCells_NA1280_Fitment_Audit_Aug2026.md
    // and SaltyCells_NA1280_Fitment_Polarity_Audit_Aug2026.md for full detail.
    //
    // Sep 2026: Ah/CCA lowered 80→45 / 1500→850 and price cut 20%
    // (439→351) at Jeremy's direction — stays above AESON's case-matched
    // 95D31L/R (33Ah/650CCA/$395). See
    // SaltyCells_Spec_Price_Revision_Sep2026.md.
    //
    // Sep 2026 (4th pass): Ah corrected 45→40 to match Yichen's real
    // supply (PI YC20260911001 S/N 4 quotes 40Ah for this exact case,
    // 306x174x220mm — an exact dimension match) — CCA scaled
    // proportionally 850→750, Wh recalculated at 12.4V. Still clears
    // AESON's 95D31L/R comparator (33Ah/650CCA) with room to spare. Price
    // rebuilt from real landed cost (~$199 AUD incl. prorated freight) to
    // hit ~40% margin at Jeremy's direction — moved DOWN 351→335 (this SKU
    // was already running ~44% margin on the new lower cost basis).
    // Polarity on this PI line is still "pending confirmation" from
    // Yichen — Jeremy's call to assume RHP (matches this SKU's established
    // majority-stock polarity) pending written confirmation. See
    // SaltyCells_Yichen_PI20260911001_Review_Sep2026.md.
  },
  "SC-NA1260L": {
    name:     "SaltyCell NA1260L",
    group:    "12V 30Ah",
    cca:      650,
    ah:       30,
    wh:       372,
    weight:   "4.5 kg",
    l: 230, w: 175, h: 200,
    cycleLife: "3,000+",
    warranty:  "5 Years",
    price:    275,
    sku:      "SC-NA1260L",
    tag:      "Compact Cars (RHP)",
    caseClass: "JIS D23L",
    stripeUrl: "/cart/50500652105943:1?checkout",
    preorderOnly: true, // Sep 2026 (5th pass): all live SKUs switched to pre-order, ~30 day wait — see header comment
    waitDays: 30,
    // Polarity corrected Aug 2026: independent JIS-standard verification
    // (Yuasa's own naming guide + JIS D5301) found the "L" suffix denotes
    // Right-Hand Positive, not Left-Hand Positive as previously listed —
    // NA1260L is the RHP unit, NA1260 is LHP. Fitment list fully rebuilt
    // against real per-vehicle AU retailer/OEM sources in the same pass —
    // see the carDataLHP comment block below for the full methodology note.
    //
    // Sep 2026: Ah/CCA lowered 60→30 / 1300→650 and price cut 20%
    // (349→279) at Jeremy's direction, mirroring SC-NA1260 — stays above
    // AESON's case-matched 55D23L (20Ah/450CCA/$318). See
    // SaltyCells_Spec_Price_Revision_Sep2026.md.
    //
    // Sep 2026 (4th pass): 30Ah already matches Yichen's real supply
    // exactly (PI YC20260911001, S/N 2), no Ah change. Price rebuilt from
    // real landed cost (~$162 AUD incl. prorated freight) to hit ~40%
    // margin, mirroring SC-NA1260 — moved DOWN 279→275. Same open
    // dimension-mismatch caveat as SC-NA1260 applies here — the PI's
    // 278x175x190mm for this line doesn't match our claimed
    // 230x175x200mm. See SaltyCells_Yichen_PI20260911001_Review_Sep2026.md.
  },
  // SC-EU1275 retired Aug 2026 — merged into SC-NA1270-280 (see that entry
  // above). Its 5 vehicles (Mercedes C-Class, Audi A3/A4, VW Golf/Polo) now
  // point to SC-NA1270-280 in carData below. Not a fitment change — the
  // 278x175x190mm case is within 1-2mm of NA1270's on every dimension.
  "SC-EU1280": {
    name:     "SaltyCell EU1280",
    group:    "12V 60Ah",
    cca:      900,
    ah:       60,
    wh:       744,
    weight:   "7.5 kg", // Yichen PI YC20260914001 S/N 5 (no longer an estimate)
    l: 315, w: 175, h: 190,
    cycleLife: "3,000+",
    warranty:  "5 Years",
    price:    435,
    sku:      "SC-EU1280",
    tag:      "European Performance",
    caseClass: "DIN75",
    features: "Built-in Bluetooth BMS",
    stripeUrl: "/cart/50500652204247:1?checkout",
    preorderOnly: true, // Sep 2026 (5th pass): all live SKUs switched to pre-order, ~30 day wait — see header comment
    waitDays: 30,
    // Fitment (case size + terminal side) confirmed via real per-vehicle
    // lookups (BMW 1 Series F20, 3 Series F30) — see
    // SaltyCells_Euro_Battery_Sizing_Research.md. CCA/weight are still
    // supplier-pending estimates (flagged "(est.)" in the spec tiles) but
    // that's a spec-confirmation detail, not a fitment uncertainty.
    //
    // Marked AVAILABLE (live) Aug 2026 at Jeremy's direction — preorderOnly
    // removed. Fitment list also expanded this pass with Ford Ranger/
    // Everest (ISS diesel) and Kia Carnival/Sorento (diesel) — see
    // SaltyCells_Ranger_Everest_Carnival_Sorento_Polarity_ReVerification_Aug2026.md.
    //
    // Renamed from SC-EU1288 to SC-EU1280 Sep 2026 at Jeremy's direction,
    // with a matching spec correction from 88Ah to 80Ah — Wh/CCA/weight
    // recalculated accordingly. This also resolves a previously-flagged
    // mismatch: the live product image (reused from SC-NA1280's image,
    // reading "80AH 1500 CCA") now matches the real Ah spec rather than
    // contradicting it. Live Stripe product and
    // payment link updated in place (same Price/Payment Link IDs — no
    // price change, name/description/metadata only). See
    // SaltyCells_NA1270-280_EU1280_Spec_Corrections_Sep2026.md.
    //
    // Sep 2026 (second pass): Ah lowered 80→65 while CCA raised 770→900
    // (the prior 770 CCA "est." figure was a proportional scale-down
    // artifact, not a realistic figure for a DIN75-class case) and price
    // cut 20% (449→359) at Jeremy's direction — comfortably above AESON's
    // nearest larger stop-start tiers (LN4 40Ah/800CCA/$593, LN5
    // 49Ah/850CCA/$692). See SaltyCells_Spec_Price_Revision_Sep2026.md.
    //
    // Sep 2026 (4th pass): Ah corrected 65→60 to match Yichen's real
    // supply (PI YC20260911001 S/N 5, 318x176x187mm — closest match to
    // this SKU's case, though quoted under Yichen's "1280" family code,
    // not a DIN75-specific one — worth clarifying with Eva). CCA
    // deliberately NOT scaled down with Ah (kept at 900, not 830) because
    // 60Ah now exactly ties AESON's own LN6 tier (60Ah/950CCA/$769) — at a
    // scaled-down 830 CCA we'd have gone from beating every AESON tier to
    // losing on CCA to their top one. Holding CCA at 900 keeps us close
    // behind LN6 on spec while pricing far below it; still a judgment
    // call pending real cell data, not a verified lab figure. Price
    // rebuilt from real landed cost (~$260 AUD incl. prorated freight) to
    // hit ~40% margin at Jeremy's direction — moved UP 359→435 (this SKU
    // was running only ~29% margin at the old price/cost). Polarity on
    // this PI line is still "pending confirmation" from Yichen — Jeremy's
    // call to assume RHP (matches this SKU's established polarity)
    // pending written confirmation. See
    // SaltyCells_Yichen_PI20260911001_Review_Sep2026.md.
  },
  "SC-NA1270-260": {
    name:     "SaltyCell NA1270-260",
    group:    "12V 35Ah",
    cca:      700,
    ah:       35,
    wh:       434,
    weight:   "9.5 kg", // estimated — interpolated from NA1260/NA1280 weight trend, pending supplier confirmation
    l: 260, w: 173, h: 220,
    cycleLife: "3,000+",
    warranty:  "5 Years",
    price:    319, // Sep 2026: 20% price cut applied to the prior estimate; still supplier-pending on final costing
    sku:      "SC-NA1270-260",
    tag:      "Full-Size SUVs & Wagons",
    caseClass: "JIS D26",
    features: "Built-in BMS — cell monitoring & protection",
    unavailable: true, // Sep 2026: removed from active range, no Yichen supplier line — see header comment
    stripeUrl: "/products/na1270-260",
    // Consolidated Aug 2026 at Jeremy's direction: renamed from SC-NA1275
    // into the NA1270 family as the 260mm-case variant (vs. NA1270-280's
    // 279mm case). Genuinely different physical case (JIS D26-class,
    // 260x173x220mm) — NOT a physical match for NA1270-280's case, kept as
    // a separate SKU for that reason (Jeremy confirmed: "we will have to
    // have two case sizes ... same ah but different cases"). Now includes
    // a built-in BMS. See
    // SaltyCells_NA1270_EU1275_NA1275_Consolidation_Aug2026.md.
    //
    // Went LIVE Aug 2026 at Jeremy's direction: preorderOnly removed, Ah
    // rating set to 70Ah (down from the earlier 75Ah placeholder — Jeremy's
    // explicit correction when creating the live Stripe product) and a new
    // live Stripe Payment Link created (prod_VB0fOcybQ1svYZ /
    // price_1UAeeNGRSksOAgS6RnYfCFQz). Wh recalculated at 12.4V x 70Ah.
    // CCA/weight/price remain supplier-pending estimates, unchanged.
    //
    // Aug 2026 BMS/stop-start audit: NONE of this SKU's 5 vehicles
    // (Kluger all 3 gens, Tarago, LandCruiser 100 V8, Mazda CX-9) are
    // stop-start equipped in AU spec — Toyota Australia explicitly
    // disabled factory stop-start on the facelifted Kluger, and CX-9's
    // engines never got Mazda's i-stop. Feature line reworded from
    // "stop-start compatible" to "cell monitoring & protection" to avoid
    // overclaiming a capability none of the fitted vehicles use. See
    // SaltyCells_NA1270_Stop_Start_BMS_Audit_Aug2026.md.
    //
    // Added Sep 2026 (as SC-NA1275): fills the JIS "D26-class" case-size gap identified
    // during the NA1270/NA1280 fitment audits — sits directly between
    // SC-NA1260/NA1260L's D23/D23L case (230x175x200mm) and SC-NA1280's
    // D31 case (306x174x220mm). Polarity: Right-Hand Positive (RHP) — an
    // "L"-suffix JIS group code (e.g. 90D26L) consistently appeared across
    // every independently-sourced confirmed match (Toyota, Mazda, Lexus),
    // the same "L suffix = RHP" convention established for NA1260L/NA1280.
    // A minority of D26-class vehicles (Nissan Murano 2016+, Subaru Legacy/
    // Outback 3.6R) run the opposite "R"-suffix/LHP polarity — logged as a
    // future SC-NA1275L opportunity, not added here. Some candidates
    // (Lexus RX350 pre-2023, Hyundai Santa Fe older gen, Nissan Navara D22,
    // Nissan X-Trail T31) are very likely D26-class too but their exact
    // group code/polarity wasn't independently confirmed in this pass —
    // flagged for a follow-up verification rather than added on inference.
    // CCA/weight/price are estimates interpolated from the NA1260/NA1280
    // trend, pending real supplier costing — this is a data-model and
    // fitment addition, not a finalized commercial launch.
    //
    // Sep 2026 (second pass): Ah/CCA lowered 70→35 / 1400→700 and price cut
    // 20% (399→319) at Jeremy's direction — stays above AESON's case-matched
    // 55D26L/R (20Ah/450CCA/$373). See SaltyCells_Spec_Price_Revision_Sep2026.md.
  },
};

/* -------------------------------------------------------
   CAR COMPATIBILITY DATA
   Structure: Make → Model → array of year-range mappings
   Each entry: { from: year, to: year, battery: "SKU" }
   Years are model/calendar years as sold in Australia.
   NOTE: Most European-standard vehicles use DIN/BCI battery
   sizes incompatible with the JIS product range (SC-NA126x/70/80).
   A subset of VW/Audi/BMW/Mercedes models with confirmed fitment
   are now covered by the SC-EU1275/SC-EU1280 DIN-class range —
   see the "EUROPEAN (DIN)" section below. Volvo, Land Rover and
   other European makes are still not listed — fitment not yet
   researched.
------------------------------------------------------- */
const carData = {

  /* ── TOYOTA ─────────────────────────────────────── */
  "Toyota": {

    // "HiLux (Petrol)" removed Aug 2026 fitment audit: 4-cyl variants are
    // 55D23L-class (opposite polarity to SC-NA1260) while V6/larger variants
    // need a D26/D31-class case — this make/model can't be represented
    // accurately without an engine-specific field. Re-add once split by engine.
    //
    // Aug 2026 NA1280 audit: HiAce (both gens, mostly D26/Euro AGM), Kluger
    // (all gens, D26 or hybrid Euro EFB), LandCruiser 100 Series petrol
    // (D26), and Tarago (D26) removed — none physically fit the 306x174x220mm
    // D31-class case. LandCruiser 70 Series and 300 Series removed too —
    // these DO match the D31 case size but run the opposite terminal
    // polarity (105D31R/N70ZZ, no "L" suffix = LHP) to SC-NA1280's confirmed
    // RHP stock; a genuine size match, wrong polarity, same failure mode as
    // the original NA1260/NA1260L mix-up. LandCruiser Prado narrowed to
    // 2010+ only — the 2003-2009 (120 Series) generation's spec could not
    // be confirmed from an AU-specific source.
    "HiLux (Diesel 4x4)": [
      { from: 2005, to: 2015, battery: "SC-NA1280" },
    ],
    "LandCruiser 200 Series": [
      { from: 2008, to: 2021, battery: "SC-NA1280" },
    ],
    "LandCruiser Prado": [
      { from: 2010, to: 2025, battery: "SC-NA1280" },
    ],
    "Prius": [{ from: 2004, to: 2018, battery: "SC-NA1260" }],

    // Added Sep 2026 (D26-class SC-NA1275 launch): Kluger (petrol trims —
    // 2021-2025 hybrid excluded, needs a small aux battery), Tarago, and
    // LandCruiser 100 Series petrol V8 all confirmed 90D26L-class — the
    // diesel 100 Series variant showed mixed L/R data in the earlier NA1280
    // audit and stays excluded pending a dedicated check.
    //
    // Removed Sep 2026 (third pass): SC-NA1270-260 pulled from the active
    // range (no Yichen supplier line for its case size) — these 3 entries
    // commented out rather than deleted so the fitment history and the
    // 90D26L-class research isn't lost. The fitment tool now correctly
    // reports "no match" for these vehicles instead of recommending a
    // battery that can't be bought. See
    // SaltyCells_NA1270-260_Removed_From_Range_Sep2026.md.
    // "Kluger (Petrol)": [
    //   { from: 2007, to: 2013, battery: "SC-NA1270-260" },
    //   { from: 2014, to: 2020, battery: "SC-NA1270-260" },
    //   { from: 2021, to: 2025, battery: "SC-NA1270-260" },
    // ],
    // "Tarago": [
    //   { from: 2006, to: 2019, battery: "SC-NA1270-260" },
    // ],
    // "LandCruiser 100 Series (Petrol V8)": [
    //   { from: 2000, to: 2007, battery: "SC-NA1270-260" },
    // ],
  },

  /* ── MAZDA ───────────────────────────────────────── */
  "Mazda": {
    "BT-50": [
      { from: 2006, to: 2020, battery: "SC-NA1280" },
      { from: 2021, to: 2025, battery: "SC-NA1280" },
    ],

    // Added Sep 2026 (D26-class SC-NA1275 launch): CX-9 first/second gen
    // (2007-2020) confirmed 80D26L/90D26L-class. The 2021-2025 gen is a
    // separate, smaller D23-class case (already excluded from NA1280
    // above) and is NOT part of this entry.
    //
    // Removed Sep 2026 (third pass): SC-NA1270-260 pulled from the active
    // range — see the Toyota block above for the full note.
    // "CX-9 (2007-2020)": [
    //   { from: 2007, to: 2020, battery: "SC-NA1270-260" },
    // ],

    // "MX-5" removed Aug 2026 fitment audit: genuine factory battery is a
    // narrow 24-series/B24 class case (~238x129x200-225mm), not the
    // 230x175x200mm D23-class case SC-NA1260 uses. Not a fitment match.
    //
    // CX-8, CX-9 (both gens), CX-60 and CX-90 removed in the Aug 2026 NA1280
    // audit: all confirmed/inferred as JIS D23-class EFB/AGM stop-start
    // batteries (~232x173x225mm, Q85/100D23L), not the 306x174x220mm D31
    // case BT-50 uses — a genuine case-size mismatch, same failure mode as
    // the original NA1270 error.
  },

  /* ── FORD ────────────────────────────────────────── */
  "Ford": {
    // "Fiesta" removed Aug 2026 fitment audit: European DIN44/DIN53-class
    // case (~207x174x174mm), not JIS D23-class. Not a fitment match.
    //
    // Edge, Falcon (all 3 gens), and Territory all removed in the Aug 2026
    // NA1280 audit: confirmed European DIN-class cases (DIN55 through
    // DIN88H), not JIS D31 — the same root mismatch already found for
    // NA1270. Territory specifically spans at least 3 different case
    // families across its production run with conflicting polarity
    // evidence depending on generation/engine (Aug 2026 EU-range research
    // pass) — not a clean match, needs its own generation-specific audit.
    //
    // Ranger/Everest ISS-equipped diesel trims (PX2/PX3, UA/UAII,
    // 2015-2022): confirmed DIN75LH/LN4-L AGM case, ~315x175x190mm — same
    // case size/code family as SC-EU1280. Polarity confirmed RHP by direct
    // physical description from Jeremy (Aug 2026) — see
    // SaltyCells_Ranger_Everest_Carnival_Sorento_Polarity_ReVerification_Aug2026.md.
    // Non-ISS variants and the next-gen (2022+, P703/U704 platform) Ranger/
    // Everest are NOT included — different case/spec, unconfirmed.
    "Ranger (ISS Diesel)": [
      { from: 2015, to: 2022, battery: "SC-EU1280" },
    ],
    "Everest (ISS Diesel)": [
      { from: 2015, to: 2022, battery: "SC-EU1280" },
    ],
  },

  /* ── HOLDEN ──────────────────────────────────────── */
  "Holden": {
    // Aug 2026 NA1280 audit: Colorado narrowed to RG-generation 4x4 diesel
    // only (confirmed genuine N70ZZL/D31 match) — the earlier gen (2008-2011)
    // and lighter/4x2 RG variants spec a smaller NS70L/D26-class case,
    // unconfirmed at trim level, excluded. Commodore (VE/VF, DIN55-class),
    // Trailblazer (DIN85LH-class) and Ute (VE/VF-era, DIN55-class) all
    // removed — confirmed European DIN cases, not JIS D31.
    "Colorado (4x4 Diesel)": [
      { from: 2012, to: 2020, battery: "SC-NA1280" },
    ],
  },

  /* ── HYUNDAI ─────────────────────────────────────── */
  "Hyundai": {
    // Aug 2026 NA1280 audit: Palisade, Santa Fe (current gen) and Staria all
    // removed — confirmed European LN-class stop-start AGM/EFB (LN2/LN4/
    // DIN75-77), not JIS D31. Staria specifically also runs the opposite
    // terminal polarity to SC-NA1280's confirmed RHP stock.
    //
    // Aug 2026 NA1270 audit: i40 and Kona (petrol/hybrid) are the only
    // confirmed 279x175x189mm RHP matches in the Hyundai range — most other
    // models use a shorter JIS D23-class or 60Ah "L2" European case instead.
    // Kona ELECTRIC uses a small 12V auxiliary battery, not this SKU —
    // excluded from this entry.
    "i40": [{ from: 2012, to: 2019, battery: "SC-NA1270-280" }],
    "Kona (Petrol/Hybrid)": [{ from: 2018, to: 2025, battery: "SC-NA1270-280" }],
  },

  /* ── KIA ─────────────────────────────────────────── */
  "Kia": {
    // Aug 2026 NA1280 audit: Carnival and Sorento both removed — the
    // generic (non-year-filtered) retailer pages showed D31/N70 codes by
    // blending old and new generations, but once filtered to current-gen
    // model years both switch to European LN4/DIN75LH stop-start AGM.
    // Sorento Hybrid/PHEV also runs the opposite terminal polarity.
    //
    // Carnival (diesel, KA4 gen) and Sorento (diesel, MQ4 gen), 2020-2025:
    // confirmed DIN75LH/LN4-L AGM, same case/code family as Ranger/Everest
    // ISS above — RHP confirmed for that code family via Jeremy's direct
    // physical check (Aug 2026), treated as high-confidence by code-family
    // match for these two (not independently physically checked). Hybrid/
    // PHEV Sorento variants excluded — opposite polarity per NA1280 audit.
    "Carnival (Diesel)": [
      { from: 2020, to: 2025, battery: "SC-EU1280" },
    ],
    "Sorento (Diesel)": [
      { from: 2020, to: 2025, battery: "SC-EU1280" },
    ],
  },

  /* ── MITSUBISHI ──────────────────────────────────── */
  "Mitsubishi": {
    // Aug 2026 NA1280 audit: Express removed — it's a rebadged Renault
    // Trafic on a European DIN-format case (Type 115/H7, ~L3/DIN70), not
    // JIS at all. Pajero narrowed to 2007+ (NS-NX gen, confirmed D31) — the
    // earlier NM/NP gen (2000-2006) could not be confirmed at a reliable
    // AU source. Triton narrowed to 2019+ 4x4 diesel only (confirmed D31) —
    // the earlier ML/MN gen (2006-2018) showed scattered evidence of a
    // smaller D23/D26 factory fitment on lower-spec variants, unconfirmed
    // at trim level.
    "Pajero": [
      { from: 2007, to: 2021, battery: "SC-NA1280" },
    ],
    "Pajero Sport": [
      { from: 2009, to: 2015, battery: "SC-NA1280" },
      { from: 2016, to: 2025, battery: "SC-NA1280" },
    ],
    "Triton (4x4 Diesel, 2019+)": [
      { from: 2019, to: 2025, battery: "SC-NA1280" },
    ],
  },

  /* ── NISSAN ──────────────────────────────────────── */
  "Nissan": {
    // Aug 2026 NA1280 audit: Murano removed — Nissan's own OEM part numbers
    // (AYBSL-70D23, AYBGL-55D23, AYBSR-80D26) confirm D23/D26-class across
    // both generations, one to two sizes smaller than D31. Patrol removed
    // entirely: Y62 (2013-2025) genuinely runs the D31/N70 case size but
    // with the OPPOSITE terminal polarity (N70ZZX, no "L" suffix = LHP) to
    // SC-NA1280's confirmed RHP stock — a real product-range gap, not a
    // data error. Y61/GU (2000-2012) is unconfirmed (conflicting forum
    // references to a possible larger N120-class battery on the 4.8L
    // petrol variant), excluded pending direct verification.
    "Navara (V6 Diesel)": [
      { from: 2005, to: 2015, battery: "SC-NA1280" },
    ],

    /* ── JDM Sports Cars ─────────────────────────── */
    "Skyline R32 GTS": [
      { from: 1989, to: 1994, battery: "SC-NA1260" },
    ],
    
    "Skyline R33 GTS": [
      { from: 1993, to: 1998, battery: "SC-NA1260" },
    ],
    "Skyline R33 GTS-t": [
      { from: 1993, to: 1998, battery: "SC-NA1260" },
    ],
    
    "Skyline R34 GT-T": [
      { from: 1998, to: 2002, battery: "SC-NA1260" },
    ],
    
    "Silvia S13": [
      { from: 1988, to: 1993, battery: "SC-NA1260" },
    ],
    "Silvia S14": [
      { from: 1993, to: 1999, battery: "SC-NA1260" },
    ],
    "Silvia S15": [
      { from: 1999, to: 2002, battery: "SC-NA1260" },
    ],
    "180SX": [
      { from: 1988, to: 1998, battery: "SC-NA1260" },
    ],
    "200SX S14": [
      { from: 1994, to: 1999, battery: "SC-NA1260" },
    ],
    "200SX S15": [
      { from: 1999, to: 2002, battery: "SC-NA1260" },
    ],
    // "Navara (4-Cyl)" — Aug 2026 NA1270 audit: confirmed match for the
    // NP300/D23 generation (2015+, Century DIN65 MF 277x175x175mm). The
    // earlier D22 generation (2005-2014) needs a taller N70ZZ/D26-class
    // battery and has been removed from this entry. Aug 2026 BMS/stop-start
    // audit: confirmed NOT stop-start equipped in AU spec (factory battery
    // is a basic MF unit, not AGM/EFB; Jeremy confirmed from installer-
    // network knowledge) — a genuine case-size/polarity match only, no
    // stop-start-cycling demand on this vehicle.
    "Navara (4-Cyl)": [
      { from: 2015, to: 2021, battery: "SC-NA1270-280" },
      { from: 2022, to: 2025, battery: "SC-NA1270-280" },
    ],
  },

  /* ── SUBARU ──────────────────────────────────────── */
  "Subaru": {

    // 2018-2022 facelift range added Aug 2026 (75D23L confirmed via genuine
    // Subaru parts catalog). 2023+ full redesign excluded — no reliable
    // group-code data found yet.
    "XV / Crosstrek": [
      { from: 2012, to: 2017, battery: "SC-NA1260L" },
      { from: 2018, to: 2022, battery: "SC-NA1260L" },
    ],
  },

  /* ── HONDA ───────────────────────────────────────── */
  "Honda": {
    // Aug 2026 NA1280 audit: Accord removed — confirmed D23/NS60-class
    // (~75mm shorter than the D31 case), not a match. Odyssey removed —
    // likely D23/B19-class but could not be confirmed from a reliable
    // AU-specific source; flagged for a follow-up pass rather than guessed.
  },

  /* ── ISUZU ───────────────────────────────────────── */
  "Isuzu": {
    // Aug 2026 NA1280 audit: 2008-2011 D-Max removed (base/petrol trims
    // unconfirmed at trim level). 2020-2025 D-Max and 2022-2025 MU-X
    // narrowed to 3.0L variants only — the 1.9L variants appear to spec a
    // European DIN65LHX/DIN74L-class battery instead, per retailer
    // catalogs, unconfirmed at exact trim level.
    "D-Max": [
      { from: 2012, to: 2019, battery: "SC-NA1280" },
    ],
    "D-Max (3.0L, 2020+)": [
      { from: 2020, to: 2025, battery: "SC-NA1280" },
    ],
    "MU-X": [
      { from: 2013, to: 2021, battery: "SC-NA1280" },
    ],
    "MU-X (3.0L, 2022+)": [
      { from: 2022, to: 2025, battery: "SC-NA1280" },
    ],
  },

  /* ── SUZUKI ──────────────────────────────────────── */
  "Suzuki": {
    // "Jimny" removed Aug 2026 fitment audit: 2000-2018 gen is NS40Z/B19-class,
    // 2019-2025 (JB74) gen is NS60L/B24-class — neither matches the
    // 230x175x200mm D23-class case SC-NA1260 uses. Not a fitment match.
    
  },

  /* ── GWM / HAVAL ─────────────────────────────────── */
  "GWM / Haval": {
    // Aug 2026 NA1280 audit: GWM Ute (Cannon/Cannon-X) confirmed genuine
    // D31/N70 match (N-115D31L/JE, N70ZZL) across every AU source checked.
    // Tank 300 removed — no AU retailer or owner's-manual fitment data
    // could be located; flagged for a follow-up pass rather than guessed.
    "GWM Ute": [
      { from: 2021, to: 2025, battery: "SC-NA1280" },
    ],
  },

  /* ── LEXUS ───────────────────────────────────────── */
  "Lexus": {
    // Aug 2026 NA1280 audit: IS (D26-class), NX (LN3-EFB petrol / LN2
    // hybrid), and RX 2023+ (LN3-EFB/AGM) all removed — European/D26-class,
    // not JIS D31. LX narrowed to LX570 (2008-2021) only, confirmed genuine
    // D31/N70 match — LX600 (2022-2025) could not be confirmed from a
    // reliable AU source, excluded pending follow-up.
    //
    // LDV Mifa 9 (BEV) removed from the range entirely in this pass — no AU
    // battery retailer or manual carries fitment data for it, and as a BEV
    // its "12V battery" is very likely a small auxiliary AGM, not a full
    // starting battery in the same class as anything SaltyCells sells.
    "LX": [
      { from: 2008, to: 2021, battery: "SC-NA1280" },
    ],

    // Added Sep 2026 (D26-class SC-NA1275 launch): NX pre-facelift (2014-2021,
    // ZL10 gen) confirmed 110D26L/S95-class. The redesigned 2022-2025 NX is
    // a separate European LN3-EFB case (already excluded from NA1280 above)
    // and is NOT part of this entry. IS (2005-2013) also showed D26-class
    // evidence but without a confirmed "L"/"R" suffix — excluded here
    // pending a dedicated polarity check rather than assumed.
    //
    // Removed Sep 2026 (third pass): SC-NA1270-260 pulled from the active
    // range — see the Toyota block above (in carData.Toyota) for the full
    // note.
    // "NX (2014-2021)": [
    //   { from: 2014, to: 2021, battery: "SC-NA1270-260" },
    // ],
  },

  /* ── BMW (DIN-class — confirmed via per-vehicle fitment lookup, Aug 2026) ── */
  "BMW": {
    "1 Series (118i-125i)": [
      { from: 2011, to: 2019, battery: "SC-EU1280" }, // F20; M135i confirmed 80-90Ah/315mm — base petrol variants assumed same class, verify before ordering
    ],
    "3 Series": [
      { from: 2012, to: 2019, battery: "SC-EU1280" }, // F30 318i/328i confirmed 80-96Ah/315mm
    ],
  },

  /* ── MERCEDES-BENZ (DIN-class — confirmed via per-vehicle fitment lookup, Aug 2026) ── */
  "Mercedes-Benz": {
    "C-Class": [
      { from: 2014, to: 2021, battery: "SC-NA1270-280" }, // W205 C200 confirmed 70Ah/278mm
    ],
  },

  /* ── AUDI (DIN-class — confirmed via per-vehicle fitment lookup, Aug 2026) ── */
  "Audi": {
    // Aug 2026 BMS/stop-start audit: case-size/polarity match holds across
    // the full stated range for both models below, but factory stop-start
    // was NOT fitted for the full span — A3 8P only gained it from the
    // ~2009 facelift on select TFSI engines; A4 B8 only from the ~2011/2012
    // "B8.5" facelift. Earlier years in each range are a genuine fitment
    // match without the stop-start-cycling demand. See
    // SaltyCells_NA1270_Stop_Start_BMS_Audit_Aug2026.md.
    "A3": [
      { from: 2003, to: 2020, battery: "SC-NA1270-280" }, // 8P Sportback 2.0 TDI confirmed 70-75Ah/278mm; stop-start only from ~2009 facelift on
    ],
    "A4": [
      { from: 2008, to: 2016, battery: "SC-NA1270-280" }, // B8 Avant 2.0 TDI confirmed 70-80Ah/278mm; stop-start only from ~2011/2012 "B8.5" facelift on
    ],
  },

  /* ── VOLKSWAGEN (DIN-class — confirmed via per-vehicle fitment lookup, Aug 2026) ── */
  "Volkswagen": {
    "Golf": [
      { from: 2013, to: 2020, battery: "SC-NA1270-280" }, // Mk7 1.6 TDI confirmed 70Ah/278mm
    ],
    "Polo": [
      { from: 2018, to: 2025, battery: "SC-NA1270-280" }, // Mk6 confirmed 70Ah/278mm
      // Older Polo Mk4 (2002-2007) needs a smaller ~207mm/52-55Ah case not
      // currently stocked (would be DIN44/53-class) — not listed since it
      // does NOT physically fit the EU1275's 278mm case.
    ],
  },

  /* ── VOLVO ──────────────────────────────────────────── */
  /* Not listed — DIN fitment not yet researched for this make. */

  /* ── JEEP ───────────────────────────────────────────── */
  "Jeep": {

    // "Compass" — Aug 2026 NA1270 audit: confirmed 279x175x189mm RHP match
    // (Century DIN65/LN3-class, Every Battery AU). Originally labelled
    // "Compass (Stop-Start)" on the assumption it was a stop-start-specific
    // trim match — a dedicated Aug 2026 BMS/stop-start audit found NO
    // evidence of factory stop-start on any AU-delivered Compass trim
    // (AU spec runs the NA 2.4L Tigershark petrol and 2.0L MultiJet diesel,
    // not the turbo engines Europe fits with stop-start), and Jeremy
    // confirmed this from his own installer-network knowledge. Renamed
    // accordingly — this is a genuine case-size/polarity match, not a
    // stop-start-specific one. See
    // SaltyCells_NA1270_Stop_Start_BMS_Audit_Aug2026.md.
    "Compass": [{ from: 2017, to: 2025, battery: "SC-NA1270-280" }],
  },

};

/* -------------------------------------------------------
   OPPOSITE-TERMINAL COMPATIBILITY DATA — SC-NA1260L
   Corrected Aug 2026: independent JIS-standard verification (Yuasa's own
   "How to Read Battery Names" guide + JIS D5301, cross-checked against a
   UK trade wholesaler reference) found the site had SC-NA1260 and
   SC-NA1260L's terminal polarity backwards. Corrected convention:
     - SC-NA1260  (no-suffix / "55D23"-class)  = Left-Hand Positive
     - SC-NA1260L (L-suffix / "55D23L"-class)  = Right-Hand Positive
   This dataset was fully rebuilt against that corrected convention, with
   every vehicle re-verified against real AU retailer fitment guides
   (Century, Battery World, Every Battery, Bond Batteries, Repco,
   Superspares) and OEM/JDM cross-references — not carried over from the
   pre-Aug-2026 list. Vehicles previously listed here were removed where
   the genuine factory battery is a different physical case size (e.g.
   D26/D31/N70/B19/B24-class, European DIN, or commercial truck classes),
   where sources could not confirm a match, or — for Hino 300/500 Series
   and Ranger Pro — where the actual factory fitment is a 24V twin-battery
   or heavy-duty truck battery entirely unlike this 12V passenger battery.
   A few vehicles needed the year range narrowed to the portion that
   actually matches (e.g. Toyota Alphard, Harrier, Hyundai Sonata,
   Mitsubishi Colt/Mirage) because the model spans multiple generations
   with different battery specs. See SaltyCells fitment audit notes
   (Aug 2026) for the full per-vehicle source list.

   Any make/model that already has a confirmed entry in carData above is
   deliberately NOT duplicated here (the merge below skips it
   automatically) — except Subaru XV/Crosstrek, which is set directly in
   carData above since that model already has other year ranges listed.
------------------------------------------------------- */
const carDataLHP = {
  "Toyota": {
    "Alphard": [{ from: 2002, to: 2008, battery: "SC-NA1260L" }],
    "Avalon": [{ from: 1995, to: 2005, battery: "SC-NA1260L" }],
    "Avensis": [{ from: 1997, to: 2018, battery: "SC-NA1260L" }],
    "Celica": [{ from: 1990, to: 2006, battery: "SC-NA1260L" }],
    "Cressida": [{ from: 1990, to: 1992, battery: "SC-NA1260L" }],
    "Harrier": [{ from: 2003, to: 2013, battery: "SC-NA1260L" }],
    "Rukus": [{ from: 2010, to: 2015, battery: "SC-NA1260L" }],
    "Vienta": [{ from: 1992, to: 1997, battery: "SC-NA1260L" }],
    // Added Aug 2026 (NA1270-removal follow-up): confirmed 55D23L/75D23L
    // (Q85-equivalent) across all AU-delivered petrol generations.
    "Camry (Petrol)": [{ from: 2006, to: 2025, battery: "SC-NA1260L" }],
    "RAV4 (Petrol)": [{ from: 2006, to: 2025, battery: "SC-NA1260L" }],
    // Corolla petrol narrowed to 2012+ (ZRE182/E210) — pre-2012 ZRE152 may
    // be a smaller B24-class case, unconfirmed, deliberately excluded.
    "Corolla (Petrol, 2012+)": [{ from: 2012, to: 2025, battery: "SC-NA1260L" }],
  },
  "Mazda": {
    "2": [{ from: 2002, to: 2025, battery: "SC-NA1260L" }],
    "626": [{ from: 1990, to: 2002, battery: "SC-NA1260L" }],
    "323 / 323 Astina / 323 Protege": [{ from: 1990, to: 1998, battery: "SC-NA1260L" }],
    "CX-7": [{ from: 2007, to: 2012, battery: "SC-NA1260L" }],
    "Eunos 30X": [{ from: 1993, to: 2003, battery: "SC-NA1260L" }],
    "Familia": [{ from: 1990, to: 2003, battery: "SC-NA1260L" }],
    "MX-6": [{ from: 1990, to: 1997, battery: "SC-NA1260L" }],
    "RX-8": [{ from: 2003, to: 2012, battery: "SC-NA1260L" }],
    // Added Aug 2026 (NA1270-removal follow-up): 55D23L confirmed across
    // Marshall Batteries, Bond Batteries and mazda6club owner references.
    "CX-5": [{ from: 2012, to: 2025, battery: "SC-NA1260L" }],
    "3": [{ from: 2003, to: 2025, battery: "SC-NA1260L" }],
    "6": [{ from: 2008, to: 2025, battery: "SC-NA1260L" }],
  },
  "Ford": {
    "Courier Crew Cab (petrol)": [{ from: 1986, to: 2006, battery: "SC-NA1260L" }],
    "Econovan Maxi (petrol)": [{ from: 2003, to: 2006, battery: "SC-NA1260L" }],
    "Festiva WB": [{ from: 1994, to: 1996, battery: "SC-NA1260L" }],
    "Laser KL / KN / KQ": [{ from: 1996, to: 2002, battery: "SC-NA1260L" }],
    "Telstar AX / AY (6-cyl)": [{ from: 1992, to: 1999, battery: "SC-NA1260L" }],
  },
  "Holden": {
    "Apollo": [{ from: 1993, to: 1997, battery: "SC-NA1260L" }],
  },
  "Hyundai": {
    "Sonata": [{ from: 1990, to: 2004, battery: "SC-NA1260L" }],
    // Added Aug 2026: i30 confirmed 55D23L/75D23L across full AU range.
    // Tucson narrowed to JM/LM gen only (2004-2015) — TL/NX4 stop-start
    // gen (2015+) uses D26L/LN3-class, a genuine gap, deliberately excluded.
    "i30": [{ from: 2007, to: 2025, battery: "SC-NA1260L" }],
    "Tucson (2004-2015)": [{ from: 2004, to: 2015, battery: "SC-NA1260L" }],
  },
  "Kia": {
    // Added Aug 2026: Cerato confirmed 55D23L/75D23L-class.
    "Cerato": [{ from: 2004, to: 2025, battery: "SC-NA1260L" }],
  },
  "Mitsubishi": {
    "Colt": [{ from: 2004, to: 2012, battery: "SC-NA1260L" }],
    "Grandis": [{ from: 2003, to: 2011, battery: "SC-NA1260L" }],
    "Mirage / Mirage G4": [{ from: 2012, to: 2020, battery: "SC-NA1260L" }],
    // Added Aug 2026: Outlander petrol narrowed to pre-2022 (55D23L
    // confirmed); current ZL-gen stop-start uses Euro DIN/LN3, excluded.
    // Eclipse Cross and Lancer confirmed primarily D23L-class.
    "Outlander (Petrol, pre-2022)": [{ from: 2006, to: 2021, battery: "SC-NA1260L" }],
    "Eclipse Cross": [{ from: 2017, to: 2025, battery: "SC-NA1260L" }],
    "Lancer": [{ from: 2007, to: 2017, battery: "SC-NA1260L" }],
  },
  "GWM / Haval": {
    "Great Wall SA220": [{ from: 2009, to: 2010, battery: "SC-NA1260L" }],
    "Great Wall V24": [{ from: 2009, to: 2012, battery: "SC-NA1260L" }],
    "Haval H2 (Conventional)": [{ from: 2015, to: 2021, battery: "SC-NA1260L" }],
  },
  "Infiniti": {
    "FX": [{ from: 2003, to: 2013, battery: "SC-NA1260L" }],
    "G": [{ from: 2003, to: 2015, battery: "SC-NA1260L" }],
  },
  "Nissan": {
    "350Z": [{ from: 2002, to: 2009, battery: "SC-NA1260L" }],
    "370Z / 370Z Roadster": [{ from: 2009, to: 2020, battery: "SC-NA1260L" }],
    "Altima": [{ from: 2013, to: 2020, battery: "SC-NA1260L" }],
    // Added Aug 2026: X-Trail split by generation — T30 (2001-07) and T32
    // (2014+, Q85 EFB) confirmed D23L; T31 (2007-13) is D26-class, a
    // genuine gap, deliberately excluded. Qashqai (Q85) and Pulsar confirmed.
    "X-Trail (T30, 2001-2007)": [{ from: 2001, to: 2007, battery: "SC-NA1260L" }],
    "X-Trail (T32, 2014+)": [{ from: 2014, to: 2025, battery: "SC-NA1260L" }],
    "Qashqai": [{ from: 2014, to: 2025, battery: "SC-NA1260L" }],
    "Pulsar": [{ from: 2013, to: 2017, battery: "SC-NA1260L" }],
  },
  "Suzuki": {
    "Grand Vitara": [{ from: 1998, to: 2018, battery: "SC-NA1260L" }],
    "Kizashi": [{ from: 2009, to: 2014, battery: "SC-NA1260L" }],
  },
  "Subaru": {
    // Added Aug 2026 (NA1270-removal follow-up): every Subaru OEM code
    // found across this range carries an "L" suffix (55D23L/65D23L/75D23L)
    // — consistent D23L match. 3.6R Legacy/Outback deliberately excluded
    // (confirmed D26-class, no current SaltyCells match — see blog post).
    // XV/Crosstrek is handled directly in carData above, not here.
    "Impreza": [{ from: 2007, to: 2025, battery: "SC-NA1260L" }],
    "Forester": [{ from: 2008, to: 2025, battery: "SC-NA1260L" }],
    // Liberty nameplate discontinued in AU Nov 2021 — range capped there.
    "Legacy / Liberty (2.5L)": [{ from: 2003, to: 2021, battery: "SC-NA1260L" }],
    "Levorg": [{ from: 2015, to: 2025, battery: "SC-NA1260L" }],
    "WRX / WRX STI": [{ from: 2014, to: 2025, battery: "SC-NA1260L" }],
    "BRZ": [{ from: 2012, to: 2025, battery: "SC-NA1260L" }],
    "Outback (2.5L)": [{ from: 2003, to: 2025, battery: "SC-NA1260L" }],
  },
  "MG": {
    // "ZS EV" (BEV) — added Sep 2026. This is the 12V AUXILIARY battery,
    // not an engine starter battery (the ZS EV has no engine) — it powers
    // lights, infotainment, contactors etc. Per an MG service technician
    // quoted on the MGEVs.com owner forum, the OE spec is European DIN
    // code "075" (60Ah, 540 CCA, flooded lead-acid) — a different case
    // (~242x175x175mm) to SC-NA1260L's JIS D23L tooling (230x175x200mm)
    // on paper. However, Jeremy physically checked his own 2021 ZS EV and
    // confirmed (1) positive terminal on the right when facing the
    // battery's front, matching SC-NA1260L's RHP convention, and (2)
    // SC-NA1260L's physical size fits the battery bay. Listed on this
    // direct physical check, which this project treats as the highest
    // confidence tier (overrides the spec-sheet size discrepancy above).
    // NOTE: MG's own workshop guidance and ZS EV owners report that
    // fitting an AGM replacement (instead of the OE flooded chemistry)
    // has caused BMS communication faults on some cars — worth flagging
    // to customers/installers as a compatibility caveat pending direct
    // in-market testing of a SaltyCells sodium-ion pack in this vehicle.
    // See SaltyCells_Product_Opportunities_Log.md for the research trail.
    "ZS EV": [{ from: 2021, to: 2025, battery: "SC-NA1260L" }],
  },
};

// Merge the LHP dataset in — never overwrite an existing (verified) model entry.
Object.keys(carDataLHP).forEach(make => {
  if (!carData[make]) carData[make] = {};
  Object.keys(carDataLHP[make]).forEach(model => {
    if (!carData[make][model]) {
      carData[make][model] = carDataLHP[make][model];
    }
  });
});

/* -------------------------------------------------------
   LOOKUP LOGIC
------------------------------------------------------- */

function getMakes() {
  return Object.keys(carData).sort();
}

function getModels(make) {
  if (!carData[make]) return [];
  return Object.keys(carData[make]).sort();
}

function getYears(make, model) {
  const ranges = carData[make]?.[model];
  if (!ranges) return [];
  const years = new Set();
  ranges.forEach(r => {
    for (let y = r.from; y <= r.to; y++) years.add(y);
  });
  return Array.from(years).sort((a, b) => b - a); // newest first
}

function findBatterySku(make, model, year) {
  const ranges = carData[make]?.[model];
  if (!ranges) return null;
  const yr = parseInt(year, 10);
  const range = ranges.find(r => yr >= r.from && yr <= r.to);
  return range ? range.battery : null;
}

function getBatterySpec(sku) {
  return batteries[sku] || null;
}

/* -------------------------------------------------------
   DOM INTERACTION
------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  const makeSelect  = document.getElementById('lookup-make');
  const modelSelect = document.getElementById('lookup-model');
  const yearSelect  = document.getElementById('lookup-year');
  const findBtn     = document.getElementById('lookup-btn');
  const resultCard  = document.getElementById('lookup-result');

  if (!makeSelect) return;

  getMakes().forEach(make => {
    const opt = document.createElement('option');
    opt.value = make;
    opt.textContent = make;
    makeSelect.appendChild(opt);
  });

  makeSelect.addEventListener('change', () => {
    const make = makeSelect.value;
    modelSelect.innerHTML = '<option value="">Select model</option>';
    yearSelect.innerHTML  = '<option value="">Select year</option>';
    modelSelect.disabled  = !make;
    yearSelect.disabled   = true;
    resultCard.classList.remove('visible');

    if (make) {
      getModels(make).forEach(model => {
        const opt = document.createElement('option');
        opt.value = model;
        opt.textContent = model;
        modelSelect.appendChild(opt);
      });
      modelSelect.disabled = false;
    }
  });

  modelSelect.addEventListener('change', () => {
    const make  = makeSelect.value;
    const model = modelSelect.value;
    yearSelect.innerHTML = '<option value="">Select year</option>';
    yearSelect.disabled  = true;
    resultCard.classList.remove('visible');

    if (make && model) {
      getYears(make, model).forEach(year => {
        const opt = document.createElement('option');
        opt.value = year;
        opt.textContent = year;
        yearSelect.appendChild(opt);
      });
      yearSelect.disabled = false;
    }
  });

  yearSelect.addEventListener('change', () => {
    findBtn.disabled = !yearSelect.value;
    resultCard.classList.remove('visible');
  });

  findBtn.addEventListener('click', () => {
    const make  = makeSelect.value;
    const model = modelSelect.value;
    const year  = yearSelect.value;
    if (!make || !model || !year) return;

    const sku  = findBatterySku(make, model, year);
    const spec = sku ? getBatterySpec(sku) : null;

    if (!spec) {
      resultCard.innerHTML = `
        <div class="callout callout--warning">
          <div class="callout__title">No match found</div>
          <p>We don't have a confirmed fitment for that combination yet. Contact us and we'll check our full fitment guide.</p>
          <a href="/pages/contact" class="btn btn--outline btn--sm" style="margin-top:1rem">Contact Us</a>
        </div>`;
      resultCard.classList.add('visible');
      return;
    }

    resultCard.innerHTML = `
      <div class="result-card__header">
        <div>
          <span class="badge badge--teal" style="margin-bottom:.5rem">✓ Direct Fit Confirmed</span>
          <h3 class="result-card__title">${spec.name}</h3>
          <p style="margin:0;font-size:.85rem;color:var(--text-muted)">${spec.group} · ${spec.tag} · SKU: ${spec.sku}</p>
        </div>
        <div class="result-card__price">A$${spec.price}<sup style="font-size:1rem">.00</sup></div>
      </div>

      ${spec.preorderOnly ? `
      <div class="callout callout--warning" style="margin-bottom:1.25rem">
        <p style="margin:0"><strong>Pre-order only — ships in ~${spec.waitDays || 30} days.</strong> Specs and price above are confirmed, not estimates. Order now and pay securely at checkout &mdash; we ship within that window.</p>
      </div>` : ''}

      <div class="result-card__specs">
        <div class="result-spec">
          <div class="result-spec__value">${spec.cca}</div>
          <div class="result-spec__label">Cold Cranking Amps</div>
        </div>
        <div class="result-spec">
          <div class="result-spec__value">${spec.ah}Ah</div>
          <div class="result-spec__label">Capacity</div>
        </div>
        <div class="result-spec">
          <div class="result-spec__value">${spec.weight}</div>
          <div class="result-spec__label">Weight</div>
        </div>
        <div class="result-spec">
          <div class="result-spec__value">${spec.cycleLife}</div>
          <div class="result-spec__label">Cycle Life</div>
        </div>
      </div>

      <p style="font-size:.82rem;color:var(--text-muted);margin-bottom:1.25rem">
        Dimensions: ${spec.l} × ${spec.w} × ${spec.h} mm &nbsp;|&nbsp;
        Warranty: ${spec.warranty} &nbsp;|&nbsp;
        Energy: ${spec.wh} Wh &nbsp;|&nbsp;
        IP65 Rated
      </p>

      <div class="result-card__actions">
        ${spec.preorderOnly ? `
        <a href="${spec.stripeUrl}" class="btn btn--primary btn--lg">Reserve Now</a>` : `
        <a href="${spec.stripeUrl}" class="btn btn--primary btn--lg" target="_blank" rel="noopener">
          🛒 Buy Now — A$${spec.price}
        </a>`}
        <a href="/pages/why-sodium" class="btn btn--secondary">Why Sodium-Ion?</a>
        <a href="/pages/install" class="btn btn--secondary">Install Guide</a>
      </div>
    `;

    resultCard.classList.add('visible');
    setTimeout(() => resultCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
  });
});
